import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { markNotificationRead, subscribeNotifications, useCore } from "@praesto/core";
import type { Notification } from "@praesto/shared-types";
import { relativeTime, timestampToMillis } from "../lib/time";

/**
 * Resolve a notification `deepLink` to a SAFE same-origin navigation target, or
 * null. Open-redirect guard: a notification doc could carry a hostile link, so
 * only allow an absolute in-app path (single leading `/`, never `//` or `/\`) or
 * a bare in-app segment scoped under the tenant. http(s)://, protocol-relative
 * `//host`, and `javascript:` are rejected.
 */
function safeInAppTarget(deepLink: string, tenantSlug: string | undefined): string | null {
  if (deepLink.startsWith("/") && !deepLink.startsWith("//") && !deepLink.startsWith("/\\")) {
    return deepLink;
  }
  if (tenantSlug && !/[/\\:]/.test(deepLink)) {
    return `/${tenantSlug}/${deepLink}`;
  }
  return null;
}

/**
 * Notification inbox bell. Subscribes to the current user's notifications via
 * the core inbox client, shows an unread-count badge, and a dropdown listing
 * the latest items. Clicking an item marks it read and, if `data.deepLink` is
 * present, navigates there (scoped under the current tenant slug).
 */
export function NotificationBell() {
  const core = useCore();
  const t = core.i18n.t;
  const navigate = useNavigate();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };
  const tenantId = tenantSlug ?? "";
  const userId = core.auth.currentUserId();

  const [items, setItems] = useState<ReadonlyArray<Notification>>([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tenantId || !userId) {
      setItems([]);
      return;
    }
    const unsub = subscribeNotifications(tenantId, userId, setItems);
    return () => unsub();
  }, [tenantId, userId]);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unreadCount = items.filter((n) => !n.readAt).length;

  async function onItemClick(n: Notification): Promise<void> {
    setOpen(false);
    // Persist read-state BEFORE navigating. This previously fired the write
    // fire-and-forget and then did `window.location.assign(target)`, whose
    // full-page reload tore down the JS context and aborted the in-flight
    // Firestore write — so a clicked notification stayed unread forever.
    // Awaiting the write and then doing a client-side navigation keeps the SPA
    // context alive (write completes) and avoids a jarring full reload.
    if (!n.readAt && tenantId) {
      try {
        await markNotificationRead(tenantId, n.id);
      } catch {
        // Non-fatal: navigate even if marking read failed (e.g. offline).
      }
    }
    const target = n.data?.deepLink ? safeInAppTarget(n.data.deepLink, tenantId) : null;
    if (target) {
      // Split path + query so client-side navigation lands on the right route
      // and forwards any deep-link search params (e.g. ?report=<id>).
      const url = new URL(target, window.location.origin);
      const search = Object.fromEntries(url.searchParams.entries());
      void navigate({ to: url.pathname as never, search: search as never });
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 min-h-touch min-w-touch items-center justify-center rounded-md text-fg-muted transition-colors duration-fast hover:bg-hover hover:text-fg-default sm:min-h-0 sm:min-w-0"
        aria-label="Notifiche"
        aria-haspopup="true"
        aria-expanded={open}
        title="Notifiche"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-mono-xs leading-4 font-medium text-center"
            aria-label={`${unreadCount} non lette`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] max-h-[70vh] overflow-y-auto bg-elevated border border-border-default rounded-lg shadow-lg z-50"
        >
          <div className="px-3 py-2 border-b border-border-default flex items-center justify-between">
            <span className="text-eyebrow text-fg-muted">Notifiche</span>
            {unreadCount > 0 && (
              <span className="text-xs text-fg-muted">{unreadCount} non lette</span>
            )}
          </div>

          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-fg-muted">Nessuna notifica</div>
          ) : (
            <>
              <ul className="divide-y divide-border-default">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void onItemClick(n)}
                      className="w-full text-left px-3 py-2.5 min-h-touch hover:bg-hover transition-colors flex gap-2"
                    >
                      {!n.readAt && (
                        <span
                          className="mt-1.5 h-2 w-2 rounded-full bg-accent flex-shrink-0"
                          aria-hidden="true"
                        />
                      )}
                      <span className={`flex-1 min-w-0 ${n.readAt ? "pl-4" : ""}`}>
                        <span
                          className={`block text-sm truncate ${
                            n.readAt ? "text-fg-default" : "font-semibold text-fg-default"
                          }`}
                        >
                          {n.title}
                        </span>
                        <span className="block text-xs text-fg-muted line-clamp-2">{n.body}</span>
                        <span className="block text-mono-xs text-fg-muted mt-0.5">
                          {relativeTime(timestampToMillis(n.createdAt), t)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {tenantId && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void navigate({ to: `/${tenantId}/home/inbox` as never });
                  }}
                  className="w-full border-t border-border-default px-3 py-2.5 text-center text-eyebrow text-accent-text hover:bg-hover transition-colors"
                >
                  Vedi tutte
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
